<template>
  <div class="position-relative edu-header" id="subheader">
    <b-container class="d-flex align-items-center justify-content-between">
      <!-- Logo -->
      <a :href="`${site}/${eduLang}`" class="edu-header__brand" target="_blank" rel="noopener">
        <img :src="require('@/assets/img/logo/logo-work-edunet.png?v=3')" alt="Edunet" class="img-fluid edu-header__logo" />
      </a>

      <!-- Desktop menu -->
      <nav class="d-none d-xl-block">
        <ul class="edu-header__menu list-unstyled">
          <li v-for="item in menu" :key="item.key">
            <router-link v-if="item.current" to="/" class="edu-header__link is-active" aria-current="page">
              {{ item.label }}
            </router-link>
            <a v-else :href="item.href" class="edu-header__link" target="_blank" rel="noopener">
              {{ item.label }}
            </a>
          </li>
        </ul>
      </nav>

      <!-- Mobile toggler -->
      <b-button
        variant="link"
        class="edu-header__toggler d-xl-none"
        aria-controls="edu-header-collapse"
        :aria-expanded="mobileOpen ? 'true' : 'false'"
        aria-label="Toggle navigation"
        @click="mobileOpen = !mobileOpen"
      >
        <b-icon icon="list" />
      </b-button>
    </b-container>

    <!-- Mobile menu -->
    <b-collapse id="edu-header-collapse" v-model="mobileOpen" class="d-xl-none">
      <b-container>
        <ul class="edu-header__mobile list-unstyled">
          <li v-for="item in menu" :key="item.key">
            <router-link v-if="item.current" to="/" class="edu-header__mobile-link is-active" aria-current="page">
              {{ item.label }}
            </router-link>
            <a v-else :href="item.href" class="edu-header__mobile-link" target="_blank" rel="noopener">
              {{ item.label }}
            </a>
          </li>
        </ul>
      </b-container>
    </b-collapse>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { EDUNET_SITE, edunetTexts, toEdunetLanguage } from '@/components/Themes/edunetTexts';

interface MenuItem {
  key: string
  label: string
  href: string
  // true = this portal (Talent Pool): internal link, always highlighted like the active page on edunet.works
  current?: boolean
}

@Component
export default class HeaderInformationNavigationEdunet extends Vue {
  mobileOpen = false;

  site = EDUNET_SITE;

  get eduLang() {
    return toEdunetLanguage(AppModule.language);
  }

  get menu(): MenuItem[] {
    const lang = this.eduLang;
    const texts = edunetTexts[lang].nav;
    const page = (path: string) => `${EDUNET_SITE}/${lang}/${path}`;

    return [
      { key: 'welcome', label: texts.welcome, href: page('welcome') },
      { key: 'educational-network', label: texts.educationalNetwork, href: page('educational-network') },
      { key: 'job-placement', label: texts.jobPlacement, href: page('job-placement') },
      { key: 'learn-german', label: texts.learnGerman, href: page('learn-german') },
      {
        key: 'talent-pool', label: texts.talentPool, href: '/', current: true,
      },
      { key: 'candidates', label: texts.talents, href: page('candidates') },
      { key: 'about-us', label: texts.aboutUs, href: page('about-us') },
      { key: 'contact', label: texts.contact, href: page('contact') },
    ];
  }
}
</script>

<style scoped lang="scss">
// Copy of the edunet.works navbar (#navbar-main + .menu--main); width follows our container.
.edu-header {
  --edu-text: #1a1a1a;
  --edu-border: #e5e5e5;
  --edu-accent-light: color-mix(in srgb, var(--portal-primary) 12%, #fff);

  background-color: #fff !important;
  border-bottom: 1px solid var(--edu-border);
  padding: 20px 0;
}

.edu-header__brand {
  display: block;
  flex-shrink: 0;
  margin-right: 1.5rem;
}

.edu-header__logo {
  display: block;
  max-width: 160px;

  @media (min-width: 992px) {
    max-width: 200px;
  }

  @media (min-width: 1400px) {
    max-width: 260px;
  }
}

.edu-header__menu {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
}

.edu-header__link {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  color: var(--edu-text);
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition: all 0.2s ease;

  // tighter until the container gets wide enough for the full padding
  @media (max-width: 1399.98px) {
    padding: 0.25rem 0.5rem;
  }

  &:hover,
  &:focus {
    color: var(--portal-primary);
    background-color: var(--edu-accent-light);
    text-decoration: none;
  }

  &.is-active {
    color: var(--portal-primary);
    font-weight: 600;
  }

  // orange line under the hovered / active item
  &:hover::after,
  &.is-active::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 100%;
    height: 2px;
    border-radius: 1px;
    background-color: var(--portal-primary);
    pointer-events: none;
  }
}

.edu-header__toggler {
  padding: 0.25rem 0.5rem;
  color: var(--edu-text);
  font-size: 1.75rem;
  line-height: 1;

  &:hover,
  &:focus {
    color: var(--portal-primary);
  }
}

.edu-header__mobile {
  margin: 0;
  padding: 0.5rem 0 1rem;
}

.edu-header__mobile-link {
  display: block;
  padding: 1rem 0;
  color: var(--edu-text);
  font-weight: 600;
  text-decoration: none;

  &:hover,
  &:focus,
  &.is-active {
    color: var(--portal-primary);
    text-decoration: none;
  }
}
</style>
